import React, { useRef, useEffect } from 'react';
import toastr from 'toastr';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Spinner,
} from 'reactstrap';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Form } from '@availity/form';
import { Input } from '../../lib/form-field';
import '@availity/yup';
import * as yup from 'yup';

import * as landingTemplatesActions from '../../store/landingTemplates/actions';
import { useDispatch, useSelector } from 'react-redux';
import { actualCountry } from '../../selectors/country';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import Editor from '../../components/LandingPageEditor/Editor';
import LandingComponentInserter from '../../components/LandingPageEditor/LandingComponentInserter';

const formValidation = yup.object().shape({
  name: yup.string().required(),
  description: yup.string(),
});

const LandingTemplateEditor = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const history = useHistory();
  const location = useLocation();
  const duplicateFromId =
    (location.state && location.state.duplicateFrom) || null;
  const dispatch = useDispatch();
  const editorRef = useRef(null);

  const importInputRef = useRef(null);

  const handleImportDesign = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const editor = editorRef.current && editorRef.current.editor;
    if (!editor) {
      toastr.error('The editor is not ready yet.', 'Import design');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const design = parsed && parsed.body ? parsed : parsed.design;
        if (!design || !design.body) throw new Error('Not an Unlayer design');
        editor.loadDesign(design);
        toastr.success('Design imported. Review it and save.', 'Import design');
      } catch (error) {
        toastr.error(`Invalid design JSON: ${error.message}`, 'Import design');
      }
    };
    reader.readAsText(file);
  };

  const handleExportDesign = () => {
    const editor = editorRef.current && editorRef.current.editor;
    if (!editor) {
      toastr.error('The editor is not ready yet.', 'Export design');
      return;
    }
    editor.saveDesign((design) => {
      const fileName = String((template && template.name) || 'landing-template')
        .trim()
        .replace(/[^a-z0-9-_]+/gi, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();
      const blob = new Blob([JSON.stringify(design, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  };

  const loading = useSelector((state) => state.LandingTemplate.loading);
  const saved = useSelector((state) => state.LandingTemplate.saved);
  const landingTemplates = useSelector(
    (state) => state.LandingTemplate.landingTemplates,
  );
  const country = useSelector(actualCountry);
  const countryId = country ? country.id : null;

  const template = isEditMode
    ? landingTemplates.find((t) => t.id === id) || null
    : null;

  const sourceTemplate = duplicateFromId
    ? landingTemplates.find((t) => t.id === duplicateFromId) || null
    : null;

  useEffect(() => {
    if ((isEditMode || duplicateFromId) && countryId) {
      dispatch(landingTemplatesActions.syncLandingTemplates());
    }

    return () => {
      dispatch(landingTemplatesActions.resetState());
    };
  }, [dispatch, isEditMode, duplicateFromId, countryId]);

  useEffect(() => {
    if (saved) {
      history.push('/landing-templates');
    }
  }, [saved, history]);

  const handleSave = (values) => {
    if (editorRef.current && editorRef.current.editor) {
      editorRef.current.editor.exportHtml(({ design }) => {
        dispatch(
          landingTemplatesActions.saveLandingTemplate({
            ...values,
            design,
            id: template ? template.id : undefined,
          }),
        );
      });
    } else {
      dispatch(
        landingTemplatesActions.saveLandingTemplate({
          ...values,
          design: template ? template.design : null,
          id: template ? template.id : undefined,
        }),
      );
    }
  };

  const initialDesign = template ? template.design : sourceTemplate ? sourceTemplate.design : null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title={
              isEditMode ? 'Edit Landing Template' : 'New Landing Template'
            }
            loading={loading}
          />
          <Form
            initialValues={{
              name: template
                ? template.name || ''
                : sourceTemplate
                ? `Copia di ${sourceTemplate.name}`
                : '',
              description: template
                ? template.description || ''
                : sourceTemplate
                ? sourceTemplate.description || ''
                : '',
            }}
            validationSchema={formValidation}
            onSubmit={handleSave}
            enableReinitialize>
            {({ isValid }) => (
              <Row>
                <Col md="3">
                  <Card>
                    <CardBody>
                      <h5 className="font-size-14 mb-3">Template Info</h5>
                      {loading && (
                        <div className="text-center mb-3">
                          <Spinner color="secondary" />
                        </div>
                      )}
                      <Input type="text" name="name" id="name" required />
                      <Input
                        type="text"
                        name="description"
                        id="description"
                        label="Description"
                      />
                      <LandingComponentInserter editorRef={editorRef} />
                      <div className="mt-3">
                        <input
                          type="file"
                          accept="application/json,.json"
                          ref={importInputRef}
                          style={{ display: 'none' }}
                          onChange={handleImportDesign}
                        />
                        <Button
                          type="button"
                          color="secondary"
                          outline
                          size="sm"
                          className="w-100"
                          onClick={() =>
                            importInputRef.current &&
                            importInputRef.current.click()
                          }>
                          <i className="bx bx-import align-middle mr-1" />
                          Import design JSON
                        </Button>
                      </div>
                      <div className="mt-2">
                        <Button
                          type="button"
                          color="secondary"
                          outline
                          size="sm"
                          className="w-100"
                          onClick={handleExportDesign}>
                          <i className="bx bx-export align-middle mr-1" />
                          Export design JSON
                        </Button>
                      </div>
                      <div className="mt-3">
                        <button
                          type="submit"
                          disabled={!isValid || loading}
                          className="btn btn-dark waves-effect waves-light w-100">
                          <i className="bx bx-save font-size-16 align-middle mr-2"></i>
                          {isEditMode ? 'Save Template' : 'Create Template'}
                        </button>
                      </div>
                      <div className="mt-2">
                        <Button
                          type="button"
                          color="secondary"
                          outline
                          className="w-100"
                          onClick={() => history.push('/landing-templates')}>
                          Cancel
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="9">
                  <Card>
                    <CardBody style={{ padding: 0 }}>
                      <Editor
                        editorRef={editorRef}
                        initialDesign={initialDesign}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LandingTemplateEditor;
